import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  Phone,
  Linkedin,
  Check,
  ExternalLink,
  Loader2,
  Tag,
} from "lucide-react";
import type { ReactNode } from "react";
import { contactGender, translateContactGenderLabel } from "./contactGender";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SettingsShortcut } from "../settings/SettingsShortcut";
import type { Contact } from "../types";
import type { CrmDataProvider } from "../providers/supabase/dataProvider";

export const ContactPersonalInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const { recordTypes } = useConfigurationContext();

  if (!record) return null;

  return (
    <div>
      {record.email && <EmailRow email={record.email} />}

      {record.linkedin_url && (
        <PersonalInfoRow
          icon={<Linkedin className="w-4 h-4 text-muted-foreground" />}
          primary={
            <a
              className="underline hover:no-underline text-sm text-muted-foreground"
              href={record.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              title={record.linkedin_url}
            >
              LinkedIn
            </a>
          }
        />
      )}
      {record.phone && (
        <PersonalInfoRow
          icon={<Phone className="w-4 h-4 text-muted-foreground" />}
          primary={<span className="text-sm">{record.phone}</span>}
        />
      )}
      {record.record_type && (
        <PersonalInfoRow
          icon={<Tag className="w-4 h-4 text-muted-foreground" />}
          primary={
            <span className="flex items-center gap-1 text-sm">
              {recordTypes.find((r) => r.value === record.record_type)?.label ??
                record.record_type}
              {/* Edit the list of record types from here. LCCRM-86. */}
              <SettingsShortcut section="record_types" />
            </span>
          }
        />
      )}
      {record.loops_contact_id ? (
        <PersonalInfoRow
          icon={<ExternalLink className="w-4 h-4 text-muted-foreground" />}
          primary={
            <a
              className="underline hover:no-underline text-sm text-muted-foreground"
              href={`https://app.loops.so/contacts/${record.loops_contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Loops
            </a>
          }
        />
      ) : record.email ? (
        <LinkToLoopsButton contactId={record.id} />
      ) : null}
      {contactGender
        .map((genderOption) => {
          if (record.gender === genderOption.value) {
            return (
              <PersonalInfoRow
                key={genderOption.value}
                icon={
                  <genderOption.icon className="w-4 h-4 text-muted-foreground" />
                }
                primary={
                  <div>
                    {translateContactGenderLabel(genderOption, translate)}
                  </div>
                }
              />
            );
          }
          return null;
        })
        .filter(Boolean)}
    </div>
  );
};

const EmailRow = ({ email }: { email: string }) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <PersonalInfoRow
      icon={
        <button
          type="button"
          onClick={handleCopy}
          title={translate("crm.common.copy")}
          className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Mail className="w-4 h-4" />
          )}
        </button>
      }
      primary={
        <a
          href={`mailto:${email}`}
          className="text-sm underline hover:no-underline"
        >
          {email}
        </a>
      }
    />
  );
};

const LinkToLoopsButton = ({ contactId }: { contactId: Contact["id"] }) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);

  const handleLink = async () => {
    setLinking(true);
    try {
      await dataProvider.linkToLoops(contactId);
      notify("crm.loops.linked", { type: "success" });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    } catch {
      notify("crm.loops.link_failed", { type: "error" });
    } finally {
      setLinking(false);
    }
  };

  return (
    <PersonalInfoRow
      icon={
        linking ? (
          <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
        ) : (
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
        )
      }
      primary={
        <button
          type="button"
          onClick={handleLink}
          disabled={linking}
          className="text-sm text-muted-foreground underline hover:no-underline cursor-pointer disabled:opacity-50 disabled:cursor-wait"
        >
          {linking ? "Linking..." : "Link to Loops"}
        </button>
      }
    />
  );
};

const PersonalInfoRow = ({
  icon,
  primary,
}: {
  icon: ReactNode;
  primary: ReactNode;
}) => {
  return (
    <div className="flex flex-row items-center gap-x-2 py-1 min-h-6">
      {icon}
      <div className="flex flex-wrap gap-x-2 gap-y-0 text-sm">{primary}</div>
    </div>
  );
};
